import React, { useState } from 'react';
import {
  Shield,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Calendar,
  Plus,
  Edit,
  Trash2,
  Eye,
  Loader2,
  ListChecks,
} from 'lucide-react';
import { Button, SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import DatePicker from '../../components/client/DatePicker';
import { useAdminDataProtection } from '../../hooks/useAdminDataProtection';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const AdminDataProtection = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const {
    records,
    isLoading,
    error,
    filters,
    statusOptions,
    complianceStatusOptions,
    handleFilterChange,
    handleSearch,
    statistics: stats,
    getComplianceStatusColor
  } = useAdminDataProtection();

  const columns = [
    {
      key: 'id',
      label: 'Record ID',
      sortable: true,
      width: 'w-32',
      render: (record) => (
        <span className="font-mono text-sm font-medium text-primary-600 dark:text-primary-400">
          #{record.id.substring(0, 8)}
        </span>
      )
    },
    {
      key: 'record_type',
      label: 'Type',
      sortable: true,
      width: 'w-40',
      render: (record) => (
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">
          {record.record_type?.replace('_', ' ') || 'N/A'}
        </span>
      )
    },
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      width: 'w-64',
      render: (record) => (
        <p className="text-sm font-medium text-gray-900 dark:text-gray-50 truncate">
          {record.title || 'N/A'}
        </p>
      )
    },
    {
      key: 'data_category',
      label: 'Data Category',
      sortable: true,
      width: 'w-40',
      render: (record) => (
        <span className="text-sm text-gray-900 dark:text-gray-100 capitalize">
          {record.data_category || 'N/A'}
        </span>
      )
    },
    {
      key: 'compliance_status',
      label: 'Compliance',
      sortable: true,
      width: 'w-40',
      render: (record) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getComplianceStatusColor(record.compliance_status)}`}>
          {record.compliance_status ? record.compliance_status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'N/A'}
        </span>
      )
    },
    {
      key: 'next_review',
      label: 'Next Review',
      sortable: true,
      width: 'w-32',
      render: (record) => (
        record.next_review ? (
          <div className="flex items-center space-x-2">
            <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-50">
                {formatDate(record.next_review, 'short')}
              </div>
            </div>
          </div>
        ) : (
          <span className="text-sm text-gray-400">N/A</span>
        )
      )
    },
    {
      key: 'assigned_to',
      label: 'Assigned To',
      sortable: true,
      width: 'w-48',
      render: (record) => (
        record.assigned_to ? (
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-50 truncate">
              {record.assignedTo?.name || 'N/A'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {record.assignedTo?.email || ''}
            </p>
          </div>
        ) : (
          <span className="text-sm text-gray-400">Unassigned</span>
        )
      )
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      width: 'w-32',
      render: (record) => (
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-50">
              {formatDate(record.created_at, 'medium')}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {formatTime(record.created_at)}
            </div>
          </div>
        </div>
      )
    }
  ];

  const actions = [
    {
      label: 'View',
      icon: Eye,
      variant: 'default',
      onClick: (record) => {
        console.log('View record:', record);
      }
    },
    {
      label: 'Edit',
      icon: Edit,
      variant: 'default',
      onClick: (record) => {
        setEditingRecord(record);
        setShowCreateModal(true);
      }
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: (record) => {
        if (window.confirm('Are you sure you want to delete this record?')) {
          // deleteRecord(record.id);
        }
      }
    }
  ];

  if (error) {
    return (
      <div className="space-y-5">
        <AdminPageHeader
          title="Data Protection"
          subtitle="Manage data protection records and compliance tracking"
        />
        <div className="rounded-2xl border border-red-200 dark:border-red-800/50 bg-white dark:bg-gray-900 p-6 text-center shadow-sm">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-50 mb-2">Error Loading Data Protection Records</h3>
          <p className="text-gray-600 dark:text-gray-400">
            {error?.response?.data?.message || 'Failed to load data protection records. Please try again later.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Data Protection"
        subtitle="Manage data protection records and compliance tracking"
        actions={
          <Button
            className="!rounded-xl flex items-center gap-2"
            onClick={() => {
              setEditingRecord(null);
              setShowCreateModal(true);
            }}
          >
            <Plus className="w-4 h-4" />
            Create Record
          </Button>
        }
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.dataProtection.title}
        steps={ADMIN_PAGE_GUIDES.dataProtection.steps}
        icon={ListChecks}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Records</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-50 mt-1 tabular-nums">{stats.total_records || 0}</p>
            </div>
            <Shield className="w-8 h-8 text-gray-400" />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Compliant</p>
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1 tabular-nums">{stats.compliant || 0}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-emerald-500" />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Non-Compliant</p>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1 tabular-nums">{stats.non_compliant || 0}</p>
            </div>
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Under Review</p>
              <p className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1 tabular-nums">{stats.under_review || 0}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-amber-500" />
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="p-4 sm:p-5 space-y-5">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-950/40 p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
              <SearchSelect
                label="Status"
                value={filters.status}
                onChange={(value) => handleFilterChange('status', value)}
                options={statusOptions}
              />
              <SearchSelect
                label="Compliance Status"
                value={filters.compliance_status}
                onChange={(value) => handleFilterChange('compliance_status', value)}
                options={complianceStatusOptions}
              />
              <DatePicker
                label="From Date"
                value={filters.date_from}
                onChange={(value) => handleFilterChange('date_from', value)}
              />
              <DatePicker
                label="To Date"
                value={filters.date_to}
                onChange={(value) => handleFilterChange('date_to', value)}
              />
            </div>
          </div>

          {isLoading ? (
            <div className="py-20 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading data protection records…</p>
            </div>
          ) : (
            <DataTable
              data={records}
              columns={columns}
              actions={actions}
              isLoading={false}
              searchPlaceholder="Search by title, description, type, or category..."
              onSearch={handleSearch}
              emptyMessage="No data protection records found"
              className="!border-0 !shadow-none"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDataProtection;
